import { Fragment } from "react";
import { cn } from "../../../lib/cn";

/*
 * Text splitters for the per-word / per-letter effects in ./fx. Each renders
 * the sentence once for screen readers (sr-only) and once, split and
 * aria-hidden, for the eye, so assistive tech never reads it letter by letter.
 */

/**
 * Words as `[data-word]` spans, for fx.rise / fx.stamp. With `mask`, each word
 * sits in its own overflow-hidden box so it can rise from behind its baseline
 * (the box is padded so descenders aren't cropped).
 */
export function SplitWords({
  text,
  mask = false,
  wordClassName,
}: {
  text: string;
  mask?: boolean;
  wordClassName?: string;
}) {
  const words = text.split(" ");
  return (
    <>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {words.map((word, i) => (
          <Fragment key={i}>
            {mask ? (
              <span className="-mb-[0.18em] inline-block overflow-hidden pb-[0.18em] align-bottom">
                <span data-word className={cn("inline-block", wordClassName)}>
                  {word}
                </span>
              </span>
            ) : (
              <span data-word className={cn("inline-block", wordClassName)}>
                {word}
              </span>
            )}
            {i < words.length - 1 && " "}
          </Fragment>
        ))}
      </span>
    </>
  );
}

/**
 * Letters as `[data-char]` spans, for fx.wave / fx.type / fx.scramble. Words
 * stay unbreakable so a line can only wrap between them, never mid-word.
 */
export function SplitChars({ text, charClassName }: { text: string; charClassName?: string }) {
  const words = text.split(" ");
  return (
    <>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {words.map((word, i) => (
          <Fragment key={i}>
            <span className="inline-block whitespace-nowrap">
              {Array.from(word).map((char, j) => (
                <span key={j} data-char className={cn("fx-glyph inline-block", charClassName)}>
                  {char}
                </span>
              ))}
            </span>
            {i < words.length - 1 && " "}
          </Fragment>
        ))}
      </span>
    </>
  );
}
